<template>
  <div v-if="canCall" class="whatsapp-call">
    <woot-button
      v-tooltip="buttonTooltip"
      variant="clear"
      color-scheme="secondary"
      icon="call"
      :is-loading="isInitiating || isChecking"
      @click="handleClick"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import inboxMixin from 'shared/mixins/inboxMixin';
import alertMixin from 'shared/mixins/alertMixin';
import WhatsappCallsAPI from 'dashboard/api/whatsappCalls';
import whatsappCallSession from 'dashboard/helper/whatsappCallSession';

const PERMISSION_WINDOW_MINUTES = 5;

export default {
  mixins: [inboxMixin, alertMixin],
  props: {
    conversation: {
      type: Object,
      required: true,
    },
    contact: {
      type: Object,
      required: true,
    },
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isInitiating: false,
      isChecking: false,
      eligibility: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      accountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
    }),
    canCall() {
      const permission = this.currentUser?.permissions?.make_whatsapp_call;
      return (
        this.getAccount(this.accountId)?.enable_whatsapp_calling === true &&
        this.isAWhatsAppCloudChannel &&
        this.contact?.phone_number &&
        permission !== false &&
        this.inbox?.provider_config?.calling_enabled !== false
      );
    },
    permissionStatus() {
      const status = this.eligibility?.permission?.status;
      if (status) return status;
      return this.eligibility?.permission?.permission?.status;
    },
    startCallAction() {
      return (this.eligibility?.permission?.actions || []).find(
        action => action.action_name === 'start_call'
      );
    },
    hasMetaPermission() {
      if (this.startCallAction?.can_perform_action) return true;
      return ['permanent', 'temporary', 'granted'].includes(
        this.permissionStatus
      );
    },
    hasLocalPermission() {
      const attrs = this.conversation?.additional_attributes || {};
      const requestedAt = attrs.call_permission_requested_at;
      if (!requestedAt) return false;
      const elapsedMinutes =
        (Date.now() - new Date(requestedAt).getTime()) / 60000;
      return elapsedMinutes <= PERMISSION_WINDOW_MINUTES;
    },
    hasCallPermission() {
      return this.hasMetaPermission || this.hasLocalPermission;
    },
    buttonTooltip() {
      return this.hasCallPermission
        ? this.$t('CONVERSATION.HEADER.WHATSAPP_CALL')
        : this.$t('CONVERSATION.HEADER.REQUEST_CALL_PERMISSION');
    },
  },
  async mounted() {
    window.bus.$on(
      'whatsapp-call-permission-granted',
      this.onPermissionGranted
    );
    await this.checkEligibility();
  },
  beforeDestroy() {
    window.bus.$off(
      'whatsapp-call-permission-granted',
      this.onPermissionGranted
    );
  },
  methods: {
    async checkEligibility() {
      this.isChecking = true;
      try {
        this.eligibility = await WhatsappCallsAPI.eligibility(
          this.accountId,
          this.conversation.id
        );
      } catch (error) {
        this.eligibility = null;
      } finally {
        this.isChecking = false;
      }
    },
    async handleClick() {
      if (this.isInitiating || whatsappCallSession.isCallActive) {
        this.showAlert(this.$t('CONVERSATION.HEADER.CALL_ALREADY_ACTIVE'));
        return;
      }

      if (this.hasCallPermission) {
        await this.startCall();
        return;
      }

      await this.requestPermission();
    },
    async requestPermission() {
      this.isInitiating = true;
      try {
        await WhatsappCallsAPI.requestPermission(
          this.accountId,
          this.conversation.id
        );
        this.showAlert(
          this.$t('CONVERSATION.HEADER.CALL_PERMISSION_REQUESTED')
        );
      } catch (error) {
        this.showAlert(this.humanizeCallError(error));
      } finally {
        this.isInitiating = false;
      }
    },
    async startCall() {
      this.isInitiating = true;
      try {
        if (whatsappCallSession.isCallActive) {
          throw new Error(this.$t('CONVERSATION.HEADER.CALL_ALREADY_ACTIVE'));
        }
        const recordingEnabled =
          this.inbox?.provider_config?.recording_enabled !== false;
        const call = await whatsappCallSession.initiateOutboundCall({
          accountId: this.accountId,
          conversationId: this.conversation.id,
          recordingEnabled,
          useSip: this.inbox?.provider_config?.use_sip === true,
        });
        await this.$store.dispatch('calls/addCall', {
          accountId: this.accountId,
          callSid: call.provider_call_id,
          callId: call.id,
          provider: 'whatsapp',
          callDirection: 'outbound',
          status: 'ringing',
          conversationId: this.conversation.id,
          inboxId: this.inbox.id,
          caller: {
            name: this.contact.name,
            phone: this.contact.phone_number,
            thumbnail: this.contact.thumbnail,
          },
          isActive: true,
        });
      } catch (error) {
        this.showAlert(this.humanizeCallError(error));
      } finally {
        this.isInitiating = false;
      }
    },
    humanizeCallError(error) {
      const status = error.response?.status;
      const code = error.response?.data?.code || error.code;
      const message = error.response?.data?.error || error.message;
      if (
        status === 409 ||
        message?.toLowerCase().includes('already in a call')
      ) {
        return this.$t('CONVERSATION.HEADER.CALL_ERROR_ALREADY_ACTIVE');
      }
      if (
        code === 138000 ||
        message?.toLowerCase().includes('calling api not enabled')
      ) {
        return this.$t('CONVERSATION.HEADER.CALL_ERROR_NOT_ENABLED');
      }
      if (
        code === 138009 ||
        message?.toLowerCase().includes('permission request limit')
      ) {
        return this.$t('CONVERSATION.HEADER.CALL_ERROR_PERMISSION_LIMIT');
      }
      return this.$t('CONVERSATION.HEADER.CALL_ERROR');
    },
    onPermissionGranted(data) {
      if (data.conversation_id !== this.conversation.id) return;
      this.showAlert(this.$t('CONVERSATION.HEADER.CALL_PERMISSION_GRANTED'));
      this.startCall();
    },
  },
};
</script>

<style scoped lang="scss">
.whatsapp-call {
  display: inline-flex;
}
</style>
